'use client';

import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { Button, Input, Label } from 'components';
import { InvoicePage, Maybe } from 'gql/graphql';
import { onErrorHelper } from 'handlers/handlerMutations';
import { EditIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { useInvoiceUpdate } from 'providers/InvoicesProviders/useInvoiceUpdate';
import { FC, useState } from 'react';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import { TGenericError } from 'types/TGenericError';
import { cn } from 'utils/cn';

interface IPoCodeEdit {
  invoiceId: string;
  poCode?: Maybe<string>;
  refetch: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<InvoicePage, TGenericError>>;
}

const PoCodeEdit: FC<IPoCodeEdit> = ({ invoiceId, poCode: initialPoCode, refetch }) => {
  const t = useTranslations();

  const [poCode, setPoCode] = useState(initialPoCode || '');
  const { mutateAsync: invoiceUpdate, isPending } = useInvoiceUpdate();

  const handleUpdate = (close?: () => void) => async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await invoiceUpdate({
        invoiceId,
        invoiceInput: { poCode }
      });
      await refetch();
      close?.();
    } catch (error) {
      notifyException(error, ESpansException.BillingException);
      return onErrorHelper(t('messages.error.po_code_updating'));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPoCode(e.target.value);
  };

  return (
    <div className="flex flex-1 justify-end gap-4">
      <Popover className={'relative'}>
        {({ close }) => (
          <>
            <PopoverButton as="div" className="outline-none">
              <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.edit_po_code')}>
                <Button
                  variant="icon"
                  color="none"
                  colorIcon="blue-dark"
                  startAdornment={<EditIcon className="scale-[0.9]" />}
                />
              </div>
            </PopoverButton>
            <Transition
              enter="transition ease-out duration-75"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <PopoverPanel
                className={cn(
                  'absolute left-[0px] z-10 mt-3 origin-top-left [--anchor-gap:var(--spacing-1)]',
                  'w-fit rounded-md bg-white p-4 pt-3 shadow-custom'
                )}
              >
                <div className="flex flex-col justify-end gap-4">
                  <form onSubmit={handleUpdate(close)}>
                    <Label>{t('labels.edit_po_code')}</Label>
                    <Input
                      placeholder={t('placeholders.enter_po_code')}
                      type="text"
                      value={poCode}
                      className="!w-40"
                      autoFocus
                      onChange={handleChange}
                    />
                  </form>
                  <div>
                    <Button
                      size="small"
                      className="w-full"
                      label={t('buttons.update')}
                      onClick={handleUpdate(close)}
                      loading={isPending}
                    />
                  </div>
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};

export default PoCodeEdit;
